import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery, useRemoveQueries } from '@/hooks';
import { NestedOptionModel } from '@/utils';
import { z } from 'zod';
import {
  ExpenditureBudgetPurposeFormSchema,
  ExpenditureBudgetPurposesNestedExpenditurePurposeModel,
} from '../models';
import {
  fetchExpenditureBudgetPurpose,
  invalidateListMap,
  queryKeyMap,
} from '../utils';

/**
 *  The major items and expenditure items are tied together under a 項項目 so that the number of items to be acquired can be narrowed down.
 * majorMatter:大事項
 * revenueNature:歳出科目
 */
type ExpenditureBudgetPurposesGetParams = {
  type?: 'majorMatter' | 'revenueNature';
  referenceFiscalYear?: string;
  accountingId?: string;
  accountTitleId?: string;
  showMigrated?: true;
};

export const useExpenditureBudgetPurposeNestedExpenditurePurpose = <
  T = z.infer<typeof ExpenditureBudgetPurposesNestedExpenditurePurposeModel>,
>({
  select,
  params,
  enabled,
}: {
  select?: (
    data: z.infer<
      typeof ExpenditureBudgetPurposesNestedExpenditurePurposeModel
    >,
  ) => T;
  params: ExpenditureBudgetPurposesGetParams & {
    expenditureBudgetJurisdictionId: string;
    expenditureBudgetOrganizationId?: string;
    page?: number | string;
    pageSize?: number;
  };
  enabled?: boolean;
}) => {
  const { type = 'revenueNature', showMigrated, ...rest } = params;

  return useQuery({
    queryKey:
      queryKeyMap.expenditureBudgetPurposeNestedExpenditurePurposeOptions(
        params,
      ),
    queryFn: async () => {
      const data = await axiosGet(
        '/api/expenditure-budget-purposes/nested-expenditure-purposes/',
        {
          ...rest,
          ...(!showMigrated && { isMigrated: false }),
          isMajorMatter: type === 'majorMatter',
        },
      );
      return ExpenditureBudgetPurposesNestedExpenditurePurposeModel.parse(data);
    },
    select,
    enabled,
  });
};

export const useExpenditureBudgetPurpose = ({ id }: { id: string }) => {
  return useQuery({
    queryKey: queryKeyMap.expenditureBudgetPurpose(id),
    queryFn: async () => {
      return await fetchExpenditureBudgetPurpose(id);
    },
    enabled: !!id,
  });
};

export const useNestedExpenditureBudgetPurposeOptions = <
  T = z.infer<typeof NestedOptionModel>,
>({
  params = { type: 'revenueNature' },
  select,
  enabled,
}: {
  params?: ExpenditureBudgetPurposesGetParams;
  select?: (data: z.infer<typeof NestedOptionModel>) => T;
  enabled?: boolean;
} = {}) => {
  const { type, ...rest } = params;
  return useQuery({
    select,
    queryKey: queryKeyMap.nestedExpenditureBudgetPurposeOptions(params),
    queryFn: async () => {
      const data = await axiosGet(
        '/api/expenditure-budget-purposes/nested-options/',
        {
          ...rest,
          isMajorMatter: type === 'majorMatter',
        },
      );

      return NestedOptionModel.parse(data);
    },
    enabled,
  });
};

type FormValues = z.infer<typeof ExpenditureBudgetPurposeFormSchema>;
const ExpenditureBudgetPurposeMutationParamsSchema =
  ExpenditureBudgetPurposeFormSchema.omit({
    expenditurePurposeIds: true,
  }).extend({ expenditurePurposeId: z.string().uuid() });

export const useCreateExpenditureBudgetPurpose = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({ expenditurePurposeIds, ...rest }: FormValues) => {
      const { period, ...payload } =
        ExpenditureBudgetPurposeMutationParamsSchema.parse({
          ...rest,
          expenditurePurposeId: expenditurePurposeIds.at(-1),
        });
      const res = await axiosPost('/api/expenditure-budget-purposes/', {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
      await Promise.all(
        invalidateListMap
          .updateExpenditureBudgetPurpose()
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      return res;
    },
  });
};

export const useUpdateExpenditureBudgetPurpose = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      expenditurePurposeIds,
      ...rest
    }: FormValues & { id: string }) => {
      const { id, period, ...payload } =
        ExpenditureBudgetPurposeMutationParamsSchema.extend({
          id: z.string().uuid(),
        }).parse({
          ...rest,
          expenditurePurposeId: expenditurePurposeIds.at(-1),
        });
      const res = await axiosPatch(`/api/expenditure-budget-purposes/${id}/`, {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
      await Promise.all(
        invalidateListMap
          .updateExpenditureBudgetPurpose()
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      return res;
    },
  });
};

export const useDeleteExpenditureBudgetPurpose = () => {
  const { invalidate } = useInvalidateQuery();
  const { removeQuery } = useRemoveQueries();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axiosDelete(`/api/expenditure-budget-purposes/${id}/`);
      await Promise.all(
        invalidateListMap
          .updateExpenditureBudgetPurpose()
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      removeQuery(queryKeyMap.expenditureBudgetPurpose(id));
      return res;
    },
  });
};
